import { useState } from 'react'
import type { ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  LockOutlined,
  PlusOutlined,
  ShareAltOutlined,
} from '../../components/icons'
import { Button } from '../../components/ui/Button'
import { Chip } from '../../components/ui/Chip'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { EmptyState } from '../../components/ui/EmptyState'
import { Skeleton } from '../../components/ui/Skeleton'
import { Tabs } from '../../components/ui/Tabs'
import { useToast } from '../../components/ui/ToastProvider'
import { RowActionsMenuPortal, useRowActionsMenu } from '../../components/ui/rowActionsMenu'
import type { RfqBundleItem } from '../../lib/rfq/rfq-bundle-context'
import { useRfqBundle } from '../../lib/rfq/rfq-bundle-context'
import { toSafeImageRef } from '../../lib/thumbnail-image-ref'
import { AutoPricedCurveCard } from './components/AutoPricedCurveCard'
import { CatalogPriceBreaks } from './components/CatalogPriceBreaks'
import { CreateRevisionModal } from './components/CreateRevisionModal'
import { EditConfigurationModal } from './components/EditConfigurationModal'
import { PartActiveQuotesSection } from './components/PartActiveQuotesSection'
import { PartRevisionsSection } from './components/PartRevisionsSection'
import { PartShareModal } from './components/PartShareModal'
import { formatDate, formatPrice, formatPriceBand, formatPriceRange } from './format'
import type { PartActivityEvent, PartDetail, PartRevision, PartRevisionOrder } from './types'
import { usePartDetail } from './usePartDetail'
import { defaultPartsLibraryService } from './usePartsLibrary'
import type { PartsLibraryService } from './usePartsLibrary'

export interface PartDetailPageProps {
  /** Injectable for tests / future BFF-backed swap. */
  readonly service?: PartsLibraryService
}

const SKELETON_KEYS = ['a', 'b', 'c']

/**
 * Part detail (UID-45, UID-332/ADR-0113) — pixel-rebuild to the prototype's
 * `isLibPart`/`libPart` block (`Fictiv Platform.dc.html:1673-1852`):
 * breadcrumb, header card (name/edit-pencil, Part ID, In-RFQ chips,
 * description, action row), the branched pricing card, and Purchased
 * revisions / Active quotes / Part activity tabs.
 */
export function PartDetailPage({ service = defaultPartsLibraryService }: PartDetailPageProps = {}) {
  const { partId = '' } = useParams<{ partId: string }>()
  const { state, refetch } = usePartDetail(partId, service)
  const navigate = useNavigate()
  const rfqBundle = useRfqBundle()
  const { showToast } = useToast()
  const [tab, setTab] = useState<'revisions' | 'quotes' | 'activity'>('revisions')
  const [revisionOpen, setRevisionOpen] = useState(false)
  const [configOpen, setConfigOpen] = useState(false)
  // UID-422 — "Share part" header action.
  const [shareOpen, setShareOpen] = useState(false)
  // UID-422 — "Remove from library" header action. Mirrors `WorkspacePage.tsx`'s
  // "Delete project" `ConfirmDialog` posture: `removeOpen` gates the dialog,
  // `removeBusy` guards a fast double-click, `removeError` surfaces inside the
  // still-open dialog rather than a toast.
  const [removeOpen, setRemoveOpen] = useState(false)
  const [removeBusy, setRemoveBusy] = useState(false)
  const [removeError, setRemoveError] = useState<string | undefined>(undefined)

  if (state.status === 'loading') {
    return <PartDetailSkeleton />
  }

  if (state.status === 'not_found') {
    return (
      <EmptyState
        title="Part not found"
        detail="This part may have been removed, or you may not have access to it."
        action={<Button onClick={() => navigate('/parts')}>Back to library</Button>}
      />
    )
  }

  if (state.status === 'error') {
    return (
      <EmptyState
        title="We couldn’t load this part"
        detail={state.message}
        action={<Button onClick={refetch}>Try again</Button>}
      />
    )
  }

  const { part, activity } = state
  const name = part.sourceable.name ?? '(unnamed part)'
  const revisionsCount = part.revisionGroups?.length ?? part.revisions.length
  // W1 (code-review) — `activeQuoteCount` is the server's NON-TERMINAL count;
  // `activeQuotes.length` would also count terminal (rejected/expired)
  // entries the list may carry for this tab's own history, so the badge must
  // read `activeQuoteCount` directly rather than deriving from the array.
  const quotesCount = part.activeQuoteCount

  // UID-159 (A3) / code-review UID-332 dedup — the shared queue-into-
  // `/rfq/new` sequence `handleRequestVolumeRfq` and `handleAddRevisionToQuote`
  // both need: SAME path the Library's "Add to RFQ bundle" CTA uses
  // (PartsLibraryPage.handleAddToRfq), not a new RFQ creation path.
  // `addPartsToRfq` is the same best-effort, fire-and-forget audit flag that
  // CTA also sends; the essential step is queue + navigate. Parameterized by
  // the item(s) being queued and the sourceable ids to flag, so each caller
  // still controls exactly what gets queued.
  // UID-552 (ADR-0170) — `beginDraft`, not `addItems`: both callers
  // (`handleRequestVolumeRfq`, `handleAddRevisionToQuote`) are "Create RFQ"/
  // "Add to RFQ" entry points and must start a genuinely NEW draft.
  function queueForNewRfq(items: readonly RfqBundleItem[], sourceableIds: readonly string[]) {
    rfqBundle.beginDraft(items)
    navigate('/rfq/new')
    void service.addPartsToRfq(sourceableIds).catch(() => {})
  }

  // UID-335 follow-up — `GET /parts/:id` resolves a real/generated thumbnail
  // onto `part.thumbnailUrl`, a SIBLING field of `part.sourceable` (never
  // onto `sourceable.imageRef` itself). Without this merge, queuing this
  // part into a new RFQ lost its thumbnail even though the detail page's
  // own `<img>` (below) already renders it fine. See `toSafeImageRef`'s doc
  // comment for why the merge needs the root-relative-vs-absolute
  // distinction it applies.
  const bundleImageRef = toSafeImageRef(part.thumbnailUrl, part.sourceable.id)
  const bundleSourceable =
    bundleImageRef !== undefined
      ? { ...part.sourceable, imageRef: bundleImageRef }
      : part.sourceable

  function handleRequestVolumeRfq() {
    queueForNewRfq(
      [{ sourceable: bundleSourceable, name, sku: part.sourceable.catalogSku ?? null }],
      [part.sourceable.id],
    )
  }

  function handleAddRevisionToQuote(_revision: PartRevision) {
    queueForNewRfq(
      [{ sourceable: bundleSourceable, name, sku: part.sourceable.catalogSku ?? null }],
      [part.sourceable.id],
    )
    // S4 (code-review) — this queues the WHOLE part into the RFQ bundle
    // (`queueForNewRfq` above), not just the clicked revision — the backend
    // has no per-revision queueing. Say so honestly rather than naming the
    // specific revision the click came from.
    showToast(`${name} queued for a new quote.`, 'success')
  }

  function handleReorderOrder(_order: PartRevisionOrder) {
    // S4 (code-review) — `service.reorder` operates on the PART
    // (`part.sourceable.id`), not the specific `order` row clicked — there
    // is no order-level reorder endpoint. Name the part, not the order, so
    // the toast doesn't imply a granularity the backend can't deliver.
    service
      .reorder(part.sourceable.id)
      .then(() => showToast(`${name} added to your cart.`, 'success'))
      .catch(() => showToast('We couldn’t add this to your cart. Try again.', 'error'))
  }

  /** UID-422 "Remove from library" (header ⋮ menu). Unlike the Library table's
   * row-level remove (`PartsLibraryPage.handleConfirmRemove`, which just
   * refetches the still-visible list), this page IS the removed part's own
   * detail view — on success there is nothing left here to show, so it
   * navigates away, mirroring `WorkspacePage.tsx`'s `handleDelete` "navigate
   * away on success" pattern for whole-entity deletes. */
  async function handleConfirmRemove() {
    if (removeBusy) return
    setRemoveBusy(true)
    setRemoveError(undefined)
    try {
      await service.removePart(part.sourceable.id)
      showToast(`Removed "${name}" from your library.`, 'success')
      navigate('/parts')
    } catch (error) {
      setRemoveError(
        error instanceof Error ? error.message : 'We couldn’t remove this part. Try again.',
      )
    } finally {
      setRemoveBusy(false)
    }
  }

  return (
    <section aria-labelledby="part-detail-heading" className="part-detail-page">
      <nav aria-label="Breadcrumb" className="part-detail-breadcrumb">
        <Link to="/parts">Library</Link>
        <span aria-hidden="true"> / </span>
        <span>{name}</span>
      </nav>

      <PartDetailHeaderCard
        part={part}
        name={name}
        onRename={() => showToast('Renaming a part isn’t available yet.', 'info')}
        onShare={() => setShareOpen(true)}
        onCreateRevision={() => setRevisionOpen(true)}
        onRemoveFromLibrary={() => setRemoveOpen(true)}
      />

      <PricingCard
        part={part}
        onRequestVolumeRfq={handleRequestVolumeRfq}
        onConfigurePart={() => setConfigOpen(true)}
      />

      <Tabs
        items={[
          {
            id: 'revisions',
            label: 'Purchased revisions',
            badge: { kind: 'count', count: revisionsCount },
          },
          { id: 'quotes', label: 'Active quotes', badge: { kind: 'count', count: quotesCount } },
          { id: 'activity', label: 'Part activity' },
        ]}
        activeId={tab}
        onChange={(id) => setTab(id as 'revisions' | 'quotes' | 'activity')}
        aria-label="Part detail sections"
      >
        {(activeId) => {
          if (activeId === 'revisions') {
            return (
              <PartRevisionsSection
                part={part}
                onAddToQuote={handleAddRevisionToQuote}
                onReorderOrder={handleReorderOrder}
              />
            )
          }

          if (activeId === 'quotes') {
            return <PartActiveQuotesSection part={part} onViewQuotes={() => navigate('/rfq')} />
          }

          return <ActivityTimeline events={activity} />
        }}
      </Tabs>

      <CreateRevisionModal
        open={revisionOpen}
        onClose={() => setRevisionOpen(false)}
        part={part}
        // Same posture as the pre-UID-332 page — the modal shows its own
        // success confirmation while open; once dismissed, refetch so the
        // rest of the view reflects the new revision.
        onCreated={() => refetch()}
      />

      {shareOpen && (
        <PartShareModal
          open
          onClose={() => setShareOpen(false)}
          partId={part.sourceable.id}
          partName={name}
        />
      )}

      {/* FIX C (dc.html:6424/6426/7711 isPrompt "Config to price") — the
          same `EditConfigurationModal` `PartsTable`'s "Edit configuration"
          row action opens (UID-163/C3), mounted here so the pricing card's
          "Configure part" CTA (custom `quote_required`, no `quotedPricing`)
          has somewhere real to go — not a dead CTA. */}
      {configOpen && (
        <EditConfigurationModal
          open
          onClose={() => setConfigOpen(false)}
          part={part}
          onSaved={() => refetch()}
        />
      )}

      {/* UID-422 "Remove from library" — design copy verbatim
          (`Fictiv Platform.dc.html` ~L4549-4551 `removeModal`). */}
      <ConfirmDialog
        open={removeOpen}
        title="Remove from library?"
        description={`"${name}" and all of its revisions will be removed from your library. Purchase history and past orders are unaffected.`}
        confirmLabel={removeBusy ? 'Removing…' : 'Remove part'}
        onCancel={() => {
          setRemoveOpen(false)
          setRemoveError(undefined)
        }}
        onConfirm={handleConfirmRemove}
        error={removeOpen ? removeError : undefined}
      />
    </section>
  )
}

function PartDetailHeaderCard({
  part,
  name,
  onRename,
  onShare,
  onCreateRevision,
  onRemoveFromLibrary,
}: {
  readonly part: PartDetail
  readonly name: string
  readonly onRename: () => void
  readonly onShare: () => void
  readonly onCreateRevision: () => void
  readonly onRemoveFromLibrary: () => void
}) {
  const { open, coords, containerRef, triggerRef, panelRef, menuId, toggleOpen, runAndClose } =
    useRowActionsMenu()
  const rfqChips = part.rfqChips ?? []
  const hasRfq = rfqChips.length > 0 || part.inRfq

  return (
    <div className="card part-detail-header">
      <PartDetailThumb thumbnailUrl={part.thumbnailUrl} name={name} />
      <div className="part-detail-header-body">
        <div className="part-detail-name-row">
          <h1 id="part-detail-heading" className="display-heading">
            {name}
          </h1>
          <button
            type="button"
            className="icon-btn part-detail-rename-btn"
            aria-label="Rename part"
            onClick={onRename}
          >
            <EditOutlined aria-hidden="true" />
          </button>
        </div>
        <p className="part-detail-id-label">Part ID: {part.sourceable.id}</p>

        {hasRfq && (
          <div className="part-detail-rfq-chips">
            {rfqChips.length > 0 ? (
              <>
                <span className="part-detail-rfq-chips-label">In RFQ</span>
                {rfqChips.map((chip) => (
                  <Link key={chip.id} to={`/rfq/${chip.id}`} className="part-detail-rfq-chip">
                    {chip.locked && <LockOutlined aria-hidden="true" />}
                    {chip.label}
                  </Link>
                ))}
              </>
            ) : (
              // No rich `rfqChips` — fall back to the plain boolean `inRfq`
              // chip (pre-UID-332 treatment) rather than a redundant
              // "In RFQ" label next to an "In RFQ" chip.
              <Chip tone="purple">In RFQ</Chip>
            )}
          </div>
        )}
      </div>

      {part.description && (
        <div className="part-detail-description">
          <div className="part-detail-description-heading">Description</div>
          <p className="caption-text">{part.description}</p>
        </div>
      )}

      <div className="part-detail-header-actions" ref={containerRef}>
        <Button variant="outline-brand" size="sm" onClick={onCreateRevision}>
          <PlusOutlined aria-hidden="true" /> Create revision
        </Button>
        <Button variant="outline" size="sm" onClick={onShare}>
          Share part
        </Button>
        <button
          type="button"
          ref={triggerRef}
          className="icon-btn"
          title="More actions"
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          aria-label="More actions"
          onClick={toggleOpen}
        >
          <EllipsisOutlined aria-hidden="true" />
        </button>
        {/* S5 (code-review) — dropped the "Create revision" item that used to
            duplicate the visible button above (with a mismatched
            `RedoOutlined` glyph); the kebab now carries only the action with
            no other affordance on the page ("Remove from library"), matching
            the prototype's intent that this menu hold overflow actions, not
            a copy of what's already one click away. */}
        <RowActionsMenuPortal open={open} coords={coords} menuId={menuId} panelRef={panelRef}>
          <button
            type="button"
            className="row-actions-menu-item-danger"
            onClick={runAndClose(onRemoveFromLibrary)}
          >
            <DeleteOutlined aria-hidden="true" />
            Remove from library
          </button>
        </RowActionsMenuPortal>
      </div>
    </div>
  )
}

/**
 * Part-detail render (UID-91, screens.md §6). Unlike the library row
 * thumbnail, this image is the primary visual identifier for the part on
 * this page — there's no adjacent redundant text — so it gets a meaningful
 * `alt` (the part's name) rather than being treated as decorative. Falls
 * back to the existing styled placeholder box when there's no image (e.g.
 * `part-5`'s demo fixture, which has no image-match).
 */
function PartDetailThumb({
  thumbnailUrl,
  name,
}: {
  readonly thumbnailUrl: string | null
  readonly name: string
}) {
  if (!thumbnailUrl) {
    return <div className="part-detail-thumb" aria-hidden="true" />
  }
  return (
    <img
      src={thumbnailUrl}
      alt={name}
      width={64}
      height={64}
      className="part-detail-thumb part-detail-thumb-img"
    />
  )
}

/**
 * The pricing card's shared shell (UID-332 fix, `Fictiv Platform.dc.html
 * :1707-1720`) — every mode (quoted / locked / quote-required / auto-priced
 * curve / auto-priced breaks) renders through this ONE layout: a "PRICING"
 * caption + headline + sub-line (+ optional locked badge) on the left, the
 * mode's CTA(s) TOP-RIGHT (never bottom-left), and an optional section below
 * a divider (the curve chart or the break cards). Centralizing the shell
 * here is what keeps every mode visually aligned — no branch can quietly
 * drift to its own ad-hoc chip/layout the way the old `quote_required`
 * branch (`<Chip>Quote required</Chip>` in place of the shell) and the old
 * auto-priced branch (a green "Auto-priced" chip, no shared shell at all)
 * both had.
 */
function PricingCardShell({
  headline,
  sub,
  locked,
  lockText,
  cta,
  below,
}: {
  readonly headline: string
  readonly sub?: string
  readonly locked?: boolean
  readonly lockText?: string | null
  readonly cta?: ReactNode
  readonly below?: ReactNode
}) {
  return (
    <div className="card part-detail-pricing-card">
      <div className="part-detail-pricing-head">
        <div className="part-detail-pricing-left">
          <div className="part-detail-pricing-label">Pricing</div>
          <div className="part-detail-price">{headline}</div>
          {sub && <p className="caption-text part-detail-pricing-sub">{sub}</p>}
          {locked && (
            <span className="part-detail-lock-badge">
              <LockOutlined aria-hidden="true" />
              {lockText}
            </span>
          )}
        </div>
        {cta && <div className="part-detail-pricing-actions">{cta}</div>}
      </div>
      {below && <div className="part-detail-pricing-below">{below}</div>}
    </div>
  )
}

function PricingCard({
  part,
  onRequestVolumeRfq,
  onConfigurePart,
}: {
  readonly part: PartDetail
  readonly onRequestVolumeRfq: () => void
  readonly onConfigurePart: () => void
}) {
  const navigate = useNavigate()
  const { showToast } = useToast()
  const projection = part.sourceable.pricingProjection
  const price = part.sourceable.indicativePrice
  const quoted = part.quotedPricing
  const currency = price?.currency ?? 'USD'

  if (!projection && !quoted) {
    return (
      <div className="card part-detail-pricing-card">
        <Chip tone="neutral">Pricing pending</Chip>
        <p className="caption-text">This part hasn’t finished analysis yet.</p>
      </div>
    )
  }

  const locked = projection?.mode === 'locked_to_rfq'
  const lockText = locked
    ? projection?.lockedRfqId
      ? `Locked to ${projection.lockedRfqId}`
      : 'Locked to an in-flight RFQ'
    : null

  // AC-F2 (ADR-0113 B1) — `quotedPricing`'s presence IS the `isQuote` signal,
  // independent of `pricingProjection.mode`.
  if (quoted) {
    const headline = quoted.unitPrice
      ? formatPrice(quoted.unitPrice)
      : quoted.priceRange
        ? formatPriceBand(quoted.priceRange)
        : '—'
    return (
      <PricingCardShell
        headline={headline}
        sub={quoted.expired ? 'Quote expired' : 'Quoted price'}
        locked={locked}
        lockText={lockText}
        cta={
          <>
            <Button
              variant="outline-purple"
              size="sm"
              onClick={() => navigate(`/rfq/${quoted.rfqId}`)}
            >
              View {quoted.rfqId}
            </Button>
            {quoted.expired && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => showToast('Re-requesting a quote isn’t available yet.', 'info')}
              >
                Re-request quote
              </Button>
            )}
          </>
        }
      />
    )
  }

  if (projection?.mode === 'locked_to_rfq') {
    return (
      <PricingCardShell
        headline="—"
        sub="This part is locked to an RFQ and can’t be re-priced."
        locked={locked}
        lockText={lockText}
      />
    )
  }

  if (projection?.mode === 'quote_required') {
    // FIX C (dc.html:6424/6426/7711 `isPrompt`/needs-config) — our data
    // model has a single `quote_required` mode and can't distinguish the
    // prototype's needs-config vs. needs-rfq vs. needs-match sub-states, so
    // per direction: a CUSTOM part in `quote_required` (no `quotedPricing`,
    // already guaranteed by this point — the `if (quoted)` branch above
    // returns first) maps to the needs-config prompt. Every other
    // provenance keeps the existing "manual quote" prompt.
    if (part.sourceable.provenance === 'fictiv_custom') {
      return (
        <PricingCardShell
          headline="Config to price"
          sub="Configure process & material to generate pricing."
          cta={
            <Button variant="primary" size="sm" onClick={onConfigurePart}>
              Configure part
            </Button>
          }
        />
      )
    }
    return (
      <PricingCardShell
        headline="Quote required"
        sub="This part’s geometry needs a manual quote before pricing."
        cta={
          // dc.html:1718 `libPart.pricing.isPrompt`/`onPrompt` — the "no
          // quote to show yet" prompt CTA. Reuses the SAME
          // queue-into-`/rfq/new` handler as the auto-priced curve's
          // "Request volume RFQ" CTA (UID-159 A3) — not a new RFQ path.
          <Button variant="primary" size="sm" onClick={onRequestVolumeRfq}>
            Request quote
          </Button>
        }
      />
    )
  }

  const curve = projection?.volumeCurve
  // UID-332 fix — a catalog/MISUMI-standard part's `auto_priced` curve is
  // the "Set catalog price · volume breaks" mode (dc.html:1737-1739, static
  // Qty-break cards, no chart); every other `auto_priced` part with a curve
  // (custom/parametric parts) is the "isCurve" mode (dc.html:1722-1735, the
  // range headline + smooth-curve chart, no break cards). The two are
  // mutually exclusive — never both on the same card.
  const isBreaks = Boolean(curve) && part.sourceable.provenance === 'misumi_standard'
  const isCurve = Boolean(curve) && !isBreaks

  if (isCurve && curve) {
    const headline = formatPriceRange(currency, curve.tiers) ?? (price ? formatPrice(price) : '—')
    return (
      <PricingCardShell
        headline={headline}
        sub="Firm auto-price · scales with quantity"
        cta={
          <Button variant="outline-purple" size="sm" onClick={onRequestVolumeRfq}>
            Request volume RFQ
          </Button>
        }
        below={<AutoPricedCurveCard curve={curve} currency={currency} />}
      />
    )
  }

  if (isBreaks && curve) {
    return (
      <PricingCardShell
        headline={price ? formatPrice(price) : '—'}
        sub="Set catalog price · volume breaks"
        below={<CatalogPriceBreaks curve={curve} currency={currency} />}
      />
    )
  }

  return (
    <PricingCardShell headline={price ? formatPrice(price) : '—'} sub={`Qty ${part.quantity}`} />
  )
}

function ActivityTimeline({ events }: { readonly events: readonly PartActivityEvent[] }) {
  if (events.length === 0) {
    return <EmptyState title="No activity yet" />
  }
  return (
    <ol className="part-activity-timeline">
      {events.map((event) => (
        <li key={event.id} className="part-activity-item">
          <span className="part-activity-dot" aria-hidden="true" />
          <div>
            <p className="part-activity-label">{event.label}</p>
            <p className="caption-text">
              {formatDate(event.occurredAt)}
              {event.actor ? ` · ${event.actor}` : ''}
            </p>
          </div>
        </li>
      ))}
    </ol>
  )
}

function PartDetailSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading part detail" className="part-detail-skeleton">
      {SKELETON_KEYS.map((key) => (
        <Skeleton key={key} height={48} />
      ))}
    </div>
  )
}
